/** @graphql cc **/
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import throttle from 'lodash/throttle';
import gql from 'graphql-tag';

import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import { useSessionData } from '@confluence/session-data';

import { fireOfflineStorageAnalyticsEvent, parseErrorMessage } from '../../analytics';

import type {
	EditorOfflineStorageInsertVersionMutation,
	EditorOfflineStorageInsertVersionMutationVariables,
} from './__types__/EditorOfflineStorageInsertVersionMutation';

const i18n = defineMessages({
	VersionComment: {
		id: 'editor-offline-storage.offline-version-comment',
		defaultMessage: 'Offline edits',
		description: 'Description of document version that contains offline edits',
	},
});

// Tech-spec:
// https://hello.atlassian.net/wiki/spaces/~712020fa3d29df95e344e18f663fa84c3d65c8/pages/5567284738/Tech+Spec+Version+History+Creation+API+with+Offline+Copy
const EditorOfflineStorageInsertOfflineVersionMutation = gql`
	mutation useCreateOfflineVersionInsertVersionMutation(
		$cloudId: ID!
		$contentId: ID!
		$adfContent: String!
		$versionComment: String!
	) {
		confluence_insertOfflineVersion(
			cloudId: $cloudId
			input: { contentId: $contentId, adfContent: $adfContent, versionComment: $versionComment }
		) {
			versionNumber
			success
			errors {
				message
			}
		}
	}
`;

// Limit how often we can try to create new versions
// P50 disconnection time is ~7 seconds so this tries to minimise new versions
// if someone's connection is flaky.
const MAX_INTERVAL = 20000;

export const useCreateOfflineVersion = ({
	contentId,
	api,
}: {
	contentId: string | undefined;
	api: PublicPluginAPI<[AnalyticsPlugin]> | undefined;
}) => {
	const { cloudId } = useSessionData();
	const intl = useIntl();

	const [createOfflineVersion] = useMutation<
		EditorOfflineStorageInsertVersionMutation,
		EditorOfflineStorageInsertVersionMutationVariables
	>(EditorOfflineStorageInsertOfflineVersionMutation);

	const throttledCreateVersion = useMemo(
		() =>
			throttle(
				(args: {
					cloudId: string;
					contentId: string;
					adfContent: string;
					versionComment: string;
				}) => {
					createOfflineVersion({ variables: args })
						.then((res) => {
							if (res.errors) {
								fireOfflineStorageAnalyticsEvent({
									api,
									action: 'OFFLINE_EDITING_FAILED_STORE_RECOVERY_VERSION',
									errorMessage: res.errors.map((err) => err.message).join(','),
								});
								return;
							}
							fireOfflineStorageAnalyticsEvent({
								api,
								action: 'OFFLINE_EDITING_SUCCESS_STORE_RECOVERY_VERSION',
								success: res.data?.confluence_insertOfflineVersion?.success,
							});
						})
						.catch((e) => {
							fireOfflineStorageAnalyticsEvent({
								api,
								action: 'OFFLINE_EDITING_FAILED_STORE_RECOVERY_VERSION',
								errorMessage: parseErrorMessage(e),
							});
						});
				},
				MAX_INTERVAL,
				{ leading: true, trailing: false },
			),
		[createOfflineVersion, api],
	);

	return useCallback(
		({ doc }: { doc: PMNode }) => {
			if (!contentId) {
				fireOfflineStorageAnalyticsEvent({
					api,
					action: 'OFFLINE_EDITING_FAILED_STORE_RECOVERY_VERSION',
					errorMessage: 'No content id',
				});
				return;
			}
			const adfContent = new JSONTransformer().encode(doc);

			throttledCreateVersion({
				cloudId,
				contentId,
				adfContent: JSON.stringify(adfContent),
				versionComment: intl.formatMessage(i18n.VersionComment),
			});
		},
		[api, contentId, throttledCreateVersion, cloudId, intl],
	);
};
